import { useEffect, useRef, useState } from "react";

import { buildGoogleMapsUrl } from "../../../shared/lib/googleMapsUrl";
import { buildLocationPresentation } from "../../geo/lib/locationPresentation";
import "../../geo/widgets/geoLocationFields.css";

// DetailLocation — section 5. Canonical detail.html lines 469–500.
//
// R3.6 scope: Leaflet map lifecycle (DetailLocation-local).
//   * Mount L.map on coordinate availability
//   * Single tile layer (OSM, matched verbatim to design/js/detail.js:1–8)
//   * Single marker at the property coordinates
//   * Cleanup via map.remove() on unmount or coordinate change
//
// Leaflet is loaded as a global (window.L) via the CDN script in
// public/index.html, matching design/detail.html lines 45–48.
// No npm vendor, no react-leaflet, no map abstraction.
//
// Transport boundary:
//   * property.location.latitude
//   * property.location.longitude
//   from LocationSerializer (nullable DecimalField → string via DRF).
//   Coerced with strict guards (null / undefined / empty / NaN → no map).
//
// Degradation:
//   * No coords → muted-em-dash placeholder, no map div rendered
//   * Leaflet asset missing → early-return in effect, #map div stays empty
//   * Either failure leaves the canonical .location-map wrapper intact
//     so modernize.css spacing remains.

// Tiny local DRF Decimal coercer. NOT a shared util — DRF returns
// Decimals as strings by default; this normalizes to Number with
// strict guards so we never call Leaflet with NaN or "".
const parseDec = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const OSM_TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const OSM_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const ZOOM = 17;

function DetailLocation({ property }) {
  const location = property.location || {};
  const presentation = buildLocationPresentation(
    property.location ? property.location : null,
  );
  // Canonical special-status city is both root and settlement — render once.
  const isCanonicalSpecialCity =
    presentation.isCanonical && presentation.settlementCategory === "K";
  const lat = parseDec(location.latitude);
  const lng = parseDec(location.longitude);
  const hasCoords = lat !== null && lng !== null;
  const mapsUrl = buildGoogleMapsUrl(location.latitude, location.longitude);

  // Refs: DOM container + Leaflet instance.
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  // R8.4.3 — Leaflet CDN-missing containment. window.L is set by the
  // parser-blocking <script> in public/index.html before the React
  // bundle runs, so a one-shot mount-time probe is sufficient: the
  // dependency either exists or it failed permanently (network, SRI,
  // adblock). No polling, no detector. Kept distinct from the !hasCoords
  // branch so the user can tell "no coordinates" from "map unavailable".
  const [mapMissing, setMapMissing] = useState(false);

  useEffect(() => {
    if (!hasCoords) return undefined;
    if (typeof window === "undefined" || !window.L) {
      setMapMissing(true);
      return undefined;
    }
    if (!mapRef.current) return undefined;

    // PROPERTY-DETAIL-BLACK-SCREEN-QA-A1: contain Leaflet construct
    // failures the same way Splide is contained — an uncaught throw
    // in this effect blanks the detail route.
    let map = null;
    try {
      map = window.L.map(mapRef.current).setView([lat, lng], ZOOM);
      window.L.tileLayer(OSM_TILE_URL, {
        attribution: OSM_ATTRIBUTION,
      }).addTo(map);
      window.L.marker([lat, lng]).addTo(map);
    } catch (err) {
      console.error("DetailLocation: Leaflet mount failed", err);
      try {
        map?.remove();
      } catch (_removeErr) {
        /* ignore — partial construct */
      }
      mapInstanceRef.current = null;
      setMapMissing(true);
      return undefined;
    }

    mapInstanceRef.current = map;

    return () => {
      // map.remove() destroys tile layers, markers, listeners, and
      // releases the DOM container. Wrapped in try/catch because
      // Leaflet can throw if React unmounted the container before
      // the effect cleanup ran.
      try {
        mapInstanceRef.current?.remove();
      } catch (_err) {
        /* ignore — DOM already detached */
      }
      mapInstanceRef.current = null;
    };
  }, [hasCoords, lat, lng]);

  return (
    <section className="detail-location" id="detail-location">
      <div className="container">
        <div className="row">
          <h2 className="section-title">Локація</h2>
          <dl className="location-fields">
            <div className="location-field">
              <dt>Країна</dt>
              <dd className="location-field__value">
                {presentation.country || <span className="muted">—</span>}
              </dd>
            </div>
            {isCanonicalSpecialCity ? (
              <div className="location-field">
                <dt>Місто зі спеціальним статусом</dt>
                <dd className="location-field__value">
                  {presentation.settlement || <span className="muted">—</span>}
                </dd>
              </div>
            ) : (
              <>
                <div className="location-field">
                  <dt>Область / місто зі спеціальним статусом</dt>
                  <dd className="location-field__value">
                    {presentation.region || <span className="muted">—</span>}
                  </dd>
                </div>
                {presentation.isCanonical && presentation.administrativeContextLine ? (
                  <div className="location-field location-field--wide">
                    <dt>Адміністративний контекст</dt>
                    <dd className="location-field__value">
                      {presentation.administrativeContextLine}
                    </dd>
                  </div>
                ) : null}
                <div className="location-field">
                  <dt>Населений пункт</dt>
                  <dd className="location-field__value">
                    {presentation.settlement || <span className="muted">—</span>}
                  </dd>
                </div>
              </>
            )}
            <div className="location-field">
              <dt>Місцевість / район міста</dt>
              <dd className="location-field__value">
                {presentation.localArea || <span className="muted">—</span>}
              </dd>
            </div>
            <div className="location-field location-field--wide">
              <dt>Вулиця та номер будинку</dt>
              <dd className="location-field__value">
                {presentation.address || <span className="muted">—</span>}
              </dd>
            </div>
          </dl>
          <div className="location-map p-2 mt-3">
            {!hasCoords ? (
              <span className="muted">Локація недоступна</span>
            ) : mapMissing ? (
              <span className="muted">Карта тимчасово недоступна</span>
            ) : (
              <div
                ref={mapRef}
                id="map"
                className="w-100"
                aria-label="Карта розташування"
              />
            )}
          </div>
          {mapsUrl ? (
            <a
              className="action-btn detail-location__maps"
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="detail-google-maps"
            >
              <i className="fa-solid fa-map-location-dot" aria-hidden="true" />
              Відкрити в Google Maps
            </a>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export default DetailLocation;
